import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Input, Button, Label, Table, Spinner, Alert, Badge,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import * as XLSX from "xlsx";
import { DashboardPageHeader, EmptyState } from "../../Components/Common/DashboardKit";
import { getOtdReport } from "../../services/reportsApi";

// ---------------------------------------------------------------------------
// src/pages/KPI/OtdReport.js  (Round 11 → corrected Round 12)
//
// Two DISTINCT On-Time Delivery reports rebuilt from SSRS RDLs:
//
//   /kpi/otd/summary-otd  → SCORECARD. Same row data, grouped by business
//        segment (Company-Wide, Aerospace, Automotive, Medical, Shukla
//        Medical, Other). Each shows On Time / Late / Not Shipped counts +
//        order values + OTD %. (Matches the SSRS SummaryOTD layout.)
//
//   /kpi/otd/blown-up     → DETAIL DRILL-DOWN. A 3-slice pie (OnTime /
//        NotShipped / Late) plus a table grouped by Sales Order (SONo) with
//        expandable (+) rows revealing the order's lines. (Matches the SSRS
//        BlownUpReport layout, incl. the customer/product-group/part filters.)
//
// OTD status values from the RDL: "OnTime" | "Late" | "NotShipped".
// Both share the same backend rows; the difference is presentation.
// ---------------------------------------------------------------------------

const META = {
    "summary-otd": {
        title: "Summary On-Time Delivery",
        subtitle: "OTD scorecard by business segment",
        icon: "ri-pie-chart-2-line",
        filters: false,
        kind: "summary",
    },
    "blown-up": {
        title: "Blown-Up OTD Report",
        subtitle: "Order-level OTD detail with chart and per-order drilldown",
        icon: "ri-file-search-line",
        filters: true,
        kind: "detail",
    },
};

// Segment display order (Company-Wide first, then segments as in SSRS).
const SEGMENT_ORDER = ["Aerospace", "Medical", "Automotive", "Shukla Medical", "Other"];

const STATUS = { ON: "OnTime", LATE: "Late", NS: "NotShipped" };
const STATUS_COLOR = { OnTime: "#0ab39c", NotShipped: "#405160", Late: "#f06548" };

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmt = (v) => money.format(Number(v) || 0);
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "");

function isoOf(d) {
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d - tz).toISOString().slice(0, 10);
}
function lastMonthFirstISO() { const n = new Date(); return isoOf(new Date(n.getFullYear(), n.getMonth() - 1, 1)); }
function lastMonthEndISO()   { const n = new Date(); return isoOf(new Date(n.getFullYear(), n.getMonth(), 0)); }

function statusOf(otd) {
    const s = String(otd || "").trim().toLowerCase().replace(/\s/g, "");
    if (s === "ontime") return STATUS.ON;
    if (s === "notshipped") return STATUS.NS;
    if (s === "late") return STATUS.LATE;
    return otd || "";
}

// Aggregate rows into a scorecard bucket {onTime,late,ns, onVal,lateVal,nsVal}.
function bucketOf(rows) {
    const b = { onTime: 0, late: 0, ns: 0, onVal: 0, lateVal: 0, nsVal: 0 };
    rows.forEach((r) => {
        const v = Number(r.orderValue) || 0;
        const s = statusOf(r.otd);
        if (s === STATUS.ON) { b.onTime++; b.onVal += v; }
        else if (s === STATUS.LATE) { b.late++; b.lateVal += v; }
        else if (s === STATUS.NS) { b.ns++; b.nsVal += v; }
    });
    b.shipped = b.onTime + b.late;            // "Total Releases Shipped" = on time + late
    b.shippedVal = b.onVal + b.lateVal;
    b.totalVal = b.onVal + b.lateVal + b.nsVal;
    // OTD % = on time / (on time + late), matching SSRS (shipped basis).
    b.otdPct = b.shipped ? (b.onTime / b.shipped) * 100 : 0;
    return b;
}

// ── SummaryOTD: one scorecard block per segment ────────────────────────────
const ScoreBlock = ({ title, b }) => (
    <Card className="mb-3 shadow-sm border-0">
        <CardHeader className="bg-transparent border-bottom d-flex align-items-center py-2">
            <h6 className="mb-0 text-primary flex-grow-1">{title}</h6>
            <Badge color={b.otdPct >= 90 ? "success" : b.otdPct >= 70 ? "warning" : "danger"}>
                OTD {b.otdPct.toFixed(2)}%
            </Badge>
        </CardHeader>
        <CardBody className="p-0">
            <Table className="align-middle mb-0">
                <thead className="table-light">
                    <tr><th></th><th className="text-end">Releases</th><th className="text-end">Order Value</th></tr>
                </thead>
                <tbody>
                    <tr><td><span className="badge" style={{ background: STATUS_COLOR.OnTime }}>&nbsp;</span> On Time</td>
                        <td className="text-end font-monospace">{b.onTime}</td>
                        <td className="text-end font-monospace">{fmt(b.onVal)}</td></tr>
                    <tr><td><span className="badge" style={{ background: STATUS_COLOR.Late }}>&nbsp;</span> Late</td>
                        <td className="text-end font-monospace">{b.late}</td>
                        <td className="text-end font-monospace">{fmt(b.lateVal)}</td></tr>
                    <tr><td><span className="badge" style={{ background: STATUS_COLOR.NotShipped }}>&nbsp;</span> Not Shipped</td>
                        <td className="text-end font-monospace">{b.ns}</td>
                        <td className="text-end font-monospace">{fmt(b.nsVal)}</td></tr>
                    <tr className="table-light fw-semibold">
                        <td>Total Releases Shipped</td>
                        <td className="text-end font-monospace">{b.shipped}</td>
                        <td className="text-end font-monospace">{fmt(b.shippedVal)}</td></tr>
                </tbody>
            </Table>
        </CardBody>
    </Card>
);

const SummaryView = ({ rows }) => {
    const blocks = useMemo(() => {
        const out = [{ title: "Company Wide Order Lines (S.S. White & Shukla)", b: bucketOf(rows) }];
        SEGMENT_ORDER.forEach((seg) => {
            const segRows = rows.filter((r) => r.segment === seg);
            if (segRows.length) {
                const label = seg === "Aerospace" ? "Aerospace Order Lines (Flex + Contract)"
                    : seg === "Medical" ? "Medical Order Lines (Flex + Contract)"
                    : `${seg} Order Lines`;
                out.push({ title: label, b: bucketOf(segRows) });
            }
        });
        return out;
    }, [rows]);

    return (
        <Row>
            {blocks.map((blk, i) => (
                <Col lg={6} key={i}><ScoreBlock title={blk.title} b={blk.b} /></Col>
            ))}
        </Row>
    );
};

// ── BlownUp: pie chart + per-SONo expandable detail ────────────────────────
const DetailView = ({ rows, search }) => {
    const [open, setOpen] = useState({});
    const toggle = (so) => setOpen((p) => ({ ...p, [so]: !p[so] }));
    const expandAll = (sos) => setOpen(Object.fromEntries(sos.map((so) => [so, true])));
    const collapseAll = () => setOpen({});

    const counts = useMemo(() => {
        const c = { OnTime: 0, NotShipped: 0, Late: 0 };
        rows.forEach((r) => { const s = statusOf(r.otd); if (c[s] != null) c[s]++; });
        return c;
    }, [rows]);

    // Group rows by SONo for the expandable table.
    const groups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const map = new Map();
        rows.forEach((r) => {
            if (q && ![r.soNo, r.customer, r.partNum, r.description, r.prodGroup]
                .some((v) => String(v || "").toLowerCase().includes(q))) return;
            const key = r.soNo || "—";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        });
        return Array.from(map.entries()).map(([so, lines]) => ({
            so, lines,
            customer: lines[0]?.customer || "",
            value: lines.reduce((a, x) => a + (Number(x.orderValue) || 0), 0),
            worst: lines.some((x) => statusOf(x.otd) === STATUS.LATE) ? STATUS.LATE
                : lines.some((x) => statusOf(x.otd) === STATUS.NS) ? STATUS.NS : STATUS.ON,
        }));
    }, [rows, search]);

    return (
        <>
            <Row className="g-3 mb-3">
                <Col xl={5} lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <CardHeader className="bg-transparent border-0 pb-0"><h6 className="mb-0">OTD Chart</h6></CardHeader>
                        <CardBody>
                            <ReactApexChart type="pie" height={300}
                                series={[counts.OnTime, counts.NotShipped, counts.Late]}
                                options={{
                                    labels: ["OnTime", "NotShipped", "Late"],
                                    colors: [STATUS_COLOR.OnTime, STATUS_COLOR.NotShipped, STATUS_COLOR.Late],
                                    legend: { position: "right" },
                                    dataLabels: { enabled: true, formatter: (_v, o) => o.w.config.series[o.seriesIndex] },
                                    tooltip: { y: { formatter: (v) => `${v} releases` } },
                                }} />
                        </CardBody>
                    </Card>
                </Col>
                <Col xl={7} lg={6}>
                    <Row className="g-3 h-100">
                        {[["OnTime", counts.OnTime], ["Not Shipped", counts.NotShipped], ["Late", counts.Late]].map(([lbl, n], i) => (
                            <Col xs={4} key={i}>
                                <Card className="shadow-sm border-0 h-100 text-center"><CardBody>
                                    <p className="text-uppercase text-muted fs-12 mb-1">{lbl}</p>
                                    <h3 className="mb-0">{n}</h3>
                                </CardBody></Card>
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <CardHeader className="bg-transparent border-bottom py-3 d-flex align-items-center">
                    <h5 className="card-title mb-0 flex-grow-1"><i className="ri-list-check-2 me-2 text-primary" />Orders ({groups.length})</h5>
                    <div className="btn-group btn-group-sm" role="group">
                        <Button color="light" size="sm" onClick={() => expandAll(groups.map((g) => g.so))} disabled={groups.length === 0}><i className="ri-add-line me-1" />Expand all</Button>
                        <Button color="light" size="sm" onClick={collapseAll} disabled={groups.length === 0}><i className="ri-subtract-line me-1" />Collapse all</Button>
                    </div>
                </CardHeader>
                <CardBody className="p-0">
                    <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 40 }}></th>
                                    <th>SO #</th><th>Customer</th><th>Status</th>
                                    <th className="text-end">Lines</th><th className="text-end">Order Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.length === 0 ? (
                                    <tr><td colSpan={6}><div className="p-3"><EmptyState icon="ri-inbox-line" title="No orders for these filters" tone="secondary" compact /></div></td></tr>
                                ) : groups.map((g) => (
                                    <React.Fragment key={g.so}>
                                        <tr style={{ cursor: "pointer" }} onClick={() => toggle(g.so)}>
                                            <td><i className={`ri-arrow-${open[g.so] ? "down" : "right"}-s-line fs-18 text-primary`} /></td>
                                            <td className="fw-medium">{g.so}</td>
                                            <td>{g.customer}</td>
                                            <td><Badge style={{ background: STATUS_COLOR[g.worst] }}>{g.worst}</Badge></td>
                                            <td className="text-end">{g.lines.length}</td>
                                            <td className="text-end font-monospace">{fmt(g.value)}</td>
                                        </tr>
                                        {open[g.so] && (
                                            <tr><td colSpan={6} className="p-0 bg-light">
                                                <Table size="sm" className="mb-0">
                                                    <thead><tr className="text-muted">
                                                        <th style={{ width: 40 }}></th>
                                                        <th>Part #</th><th>Description</th><th>OTD</th>
                                                        <th>Req Ship</th><th>Status Date</th><th className="text-end">Value</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {g.lines.map((ln, j) => (
                                                            <tr key={j}>
                                                                <td></td>
                                                                <td>{ln.partNum}</td>
                                                                <td className="text-muted">{ln.description}</td>
                                                                <td><Badge style={{ background: STATUS_COLOR[statusOf(ln.otd)] || "#888" }}>{ln.otd}</Badge></td>
                                                                <td>{fmtDate(ln.reqShipDate)}</td>
                                                                <td>{fmtDate(ln.statusDate)}</td>
                                                                <td className="text-end font-monospace">{fmt(ln.orderValue)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>
        </>
    );
};

function exportToExcel(reportKey, rows) {
    const wb = XLSX.utils.book_new();
    const header = ["Status Date", "SO #", "Customer", "Part #", "Prod Code", "Line Code",
        "Segment", "Description", "OTD", "Req Ship Date", "Product Group", "Order Value"];
    const aoa = [header];
    rows.forEach((r) => aoa.push([
        fmtDate(r.statusDate), r.soNo, r.customer, r.partNum, r.prodCode, r.lineCode,
        r.segment, r.description, r.otd, fmtDate(r.reqShipDate), r.prodGroup, Number(r.orderValue) || 0,
    ]));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = header.map((h) => ({ wch: h === "Description" ? 38 : 16 }));
    XLSX.utils.book_append_sheet(wb, ws, "OTD");
    XLSX.writeFile(wb, `${reportKey}_${isoOf(new Date())}.xlsx`);
}

const OtdReport = () => {
    const { reportKey } = useParams();
    const meta = META[reportKey] || META["summary-otd"];
    document.title = `${meta.title} | SSW Technologies Dashboard`;

    const [firstDate, setFirstDate] = useState(lastMonthFirstISO());
    const [secondDate, setSecondDate] = useState(lastMonthEndISO());
    const [customer, setCustomer] = useState("");
    const [productGroup, setProductGroup] = useState("");
    const [partNum, setPartNum] = useState("");
    const [pending, setPending] = useState({
        firstDate: lastMonthFirstISO(), secondDate: lastMonthEndISO(),
        customer: "", productGroup: "", partNum: "",
    });

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    const load = useCallback(async (key, opts) => {
        setLoading(true); setError(null);
        try {
            const res = await getOtdReport(key, opts);
            setData(res);
            if (res && res.error) setError(res.error);
        } catch (e) {
            const status = e && e.response ? e.response.status : null;
            setError(status === 401 || status === 403
                ? "You don't have access to this report."
                : "Couldn't load the report. Please try again.");
            setData(null);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load(reportKey, { firstDate, secondDate, customer, productGroup, partNum });
    }, [load, reportKey, firstDate, secondDate, customer, productGroup, partNum]);

    const apply = () => {
        setFirstDate(pending.firstDate); setSecondDate(pending.secondDate);
        setCustomer(pending.customer); setProductGroup(pending.productGroup); setPartNum(pending.partNum);
    };

    const rows = useMemo(() => (data && data.rows) ? data.rows : [], [data]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon={meta.icon} eyebrow="Planning" title={meta.title}
                        subtitle={meta.subtitle} tone="teal"
                        breadcrumb={[{ label: "Dashboards", to: "/" }, { label: "KPI", to: "/kpi" }, { label: meta.title }]}
                    />

                    <Card className="mb-4 shadow-sm border-0">
                        <CardBody>
                            <Row className="g-2 align-items-end">
                                <Col xs="auto">
                                    <Label className="form-label mb-1 fw-medium"><i className="ri-calendar-event-line me-1" /> From</Label>
                                    <Input type="date" value={pending.firstDate}
                                        onChange={(e) => setPending((p) => ({ ...p, firstDate: e.target.value }))} style={{ maxWidth: 180 }} />
                                </Col>
                                <Col xs="auto">
                                    <Label className="form-label mb-1 fw-medium">To</Label>
                                    <Input type="date" value={pending.secondDate}
                                        onChange={(e) => setPending((p) => ({ ...p, secondDate: e.target.value }))} style={{ maxWidth: 180 }} />
                                </Col>
                                {meta.filters && (
                                    <>
                                        <Col xs="auto">
                                            <Label className="form-label mb-1 fw-medium">Customer</Label>
                                            <Input value={pending.customer} placeholder="All"
                                                onChange={(e) => setPending((p) => ({ ...p, customer: e.target.value }))} style={{ maxWidth: 150 }} />
                                        </Col>
                                        <Col xs="auto">
                                            <Label className="form-label mb-1 fw-medium">Product Group</Label>
                                            <Input value={pending.productGroup} placeholder="All"
                                                onChange={(e) => setPending((p) => ({ ...p, productGroup: e.target.value }))} style={{ maxWidth: 150 }} />
                                        </Col>
                                        <Col xs="auto">
                                            <Label className="form-label mb-1 fw-medium">Part #</Label>
                                            <Input value={pending.partNum} placeholder="All"
                                                onChange={(e) => setPending((p) => ({ ...p, partNum: e.target.value }))} style={{ maxWidth: 130 }} />
                                        </Col>
                                    </>
                                )}
                                <Col xs="auto">
                                    <Button color="primary" onClick={apply} disabled={loading}>
                                        {loading ? <><Spinner size="sm" className="me-1" /> Loading…</> : <><i className="ri-refresh-line me-1" /> Apply</>}
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button color="success" outline onClick={() => exportToExcel(reportKey, rows)} disabled={loading || rows.length === 0}>
                                        <i className="ri-file-excel-2-line me-1" /> Excel
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button color="light" onClick={() => window.print()} disabled={loading || rows.length === 0}>
                                        <i className="ri-printer-line me-1" /> Print
                                    </Button>
                                </Col>
                                {meta.kind === "detail" && (
                                    <Col xs="auto" className="ms-auto">
                                        <Input type="search" placeholder="Search orders…" value={search}
                                            onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 220 }} />
                                    </Col>
                                )}
                                {data && data.generatedAt && (
                                    <Col xs="auto" className={meta.kind === "detail" ? "" : "ms-auto"}>
                                        <span className="text-muted fs-12">{fmtDate(data.firstDate)} – {fmtDate(data.secondDate)}</span>
                                    </Col>
                                )}
                            </Row>
                        </CardBody>
                    </Card>

                    {error && <Alert color="danger"><i className="ri-error-warning-line me-1" /> {error}</Alert>}

                    {loading && !data && (
                        <div className="text-center py-5"><Spinner color="primary" style={{ width: 44, height: 44 }} />
                            <div className="text-muted mt-3">Loading report…</div></div>
                    )}

                    {data && rows.length === 0 && !error && (
                        <EmptyState icon="ri-inbox-line" title="No data for this date range" tone="secondary" />
                    )}

                    {data && rows.length > 0 && (
                        meta.kind === "summary"
                            ? <SummaryView rows={rows} />
                            : <DetailView rows={rows} search={search} />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default OtdReport;
